import { useState, useEffect, useRef } from 'react';

export default function BenefitsSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const benefits = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Competitive Compensation',
      description: 'Rewarding your talent and contributions with attractive remuneration.',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: 'ri-robot-line',
      title: 'Cutting-Edge Technologies',
      description: 'Work hands-on with the latest AI, automation, and Web3 innovations.',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Continuous Learning & Development',
      description: 'Access to workshops, courses, and mentorship for your professional growth.',
      color: 'from-purple-500 to-violet-500'
    },
    {
      icon: 'ri-palette-line',
      title: 'Diverse & Exciting Projects',
      description: 'Engage with a wide range of local and international clients across various industries.',
      color: 'from-pink-500 to-rose-500'
    },
    {
      icon: 'ri-team-line',
      title: 'Collaborative & Inclusive Environment',
      description: 'A supportive workplace where every voice is heard and valued.',
      color: 'from-orange-500 to-amber-500'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Prime Kochi Location',
      description: "Enjoy a vibrant work environment in the heart of Kerala's dynamic tech hub.",
      color: 'from-teal-500 to-green-500'
    },
    {
      icon: 'ri-scales-3-line',
      title: 'Work-Life Harmony',
      description: 'We believe in balancing ambitious goals with personal well-being.',
      color: 'from-indigo-500 to-purple-500'
    }
  ];

  return (
    <section ref={sectionRef} className="py-24 bg-black relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-10 w-72 h-72 bg-gradient-to-r from-orange-500/10 to-red-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-10 w-96 h-96 bg-gradient-to-r from-red-500/10 to-pink-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className={`text-center mb-20 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="text-white">Why Work </span>
            <span className="bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
              With Us?
            </span>
          </h2>
          <h3 className="text-2xl md:text-3xl text-orange-400 mb-8 font-semibold">
            Your Growth, Our Priority.
          </h3>
          <p className="text-lg text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Joining Hash Adz means becoming part of a team that's at the forefront of digital transformation. 
            We invest in our people, providing opportunities to work on exciting projects, learn cutting-edge 
            technologies like AI and Web3, and grow your expertise in a supportive environment.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className={`transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${300 + index * 150}ms` }}
            >
              <div className="bg-gray-900/50 backdrop-blur-sm p-8 rounded-2xl border border-gray-700/50 hover:border-orange-500/30 transition-all duration-300 group h-full hover:transform hover:scale-105">
                <div className={`w-16 h-16 bg-gradient-to-r ${benefit.color} rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                
                <h4 className="text-xl font-bold text-white mb-4 group-hover:text-orange-400 transition-colors duration-300">
                  {benefit.title}
                </h4>
                
                <p className="text-gray-300 leading-relaxed group-hover:text-gray-200 transition-colors duration-300">
                  {benefit.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}